// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createStateHook,
	type Action,
	createActionsHook,
	createContainer,
} from '@atlassian/react-sweet-state';

export const FlatListIssuesContainer = createContainer();

type State = {
	flatListIssues: string[];
	flatListIssueIds: string[];
	flatListChildIssueIds: string[];
};

const initialState = {
	flatListIssues: [],
	flatListIssueIds: [],
	flatListChildIssueIds: [],
};

const actions = {
	setFlatListIssueIds:
		(flatListIssueIds: string[]): Action<State> =>
		({ setState }) => {
			setState({ flatListIssueIds });
		},

	setFlatListIssues:
		(flatListIssues: string[]): Action<State> =>
		({ setState }) => {
			setState({ flatListIssues });
		},

	mergeFlatListIssues:
		(flatListIssues: string[]): Action<State> =>
		({ setState, getState }) => {
			const existingIssues = getState().flatListIssues;
			if (existingIssues.length === 0) {
				setState({ flatListIssues });
				return;
			}

			// Filter out existing issues
			const existingIssuesSet = new Set(existingIssues);
			const uniqueNewIssues = flatListIssues.filter((issue) => !existingIssuesSet.has(issue));
			if (uniqueNewIssues.length === 0) return;

			// Determine if new issues have been added to the beginning or end of the list and prepend/append accordingly
			if (existingIssues[0] !== flatListIssues[0]) {
				setState({ flatListIssues: uniqueNewIssues.concat(existingIssues) });
			} else {
				setState({ flatListIssues: existingIssues.concat(uniqueNewIssues) });
			}
		},

	setFlatListChildIssueIds:
		(flatListChildIssueIds: string[]): Action<State> =>
		({ setState }) => {
			setState({ flatListChildIssueIds });
		},
};

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	containedBy: FlatListIssuesContainer,
	initialState,
	actions,
	name: 'jira-native-issue-table-flat-list-issues-store',
});

export const useFlatListIssuesActions = createActionsHook(Store);
export const useFlatListIssuesStore = createHook(Store);

const getFlatListIssuesSelector = (state: State) => state.flatListIssues;

export const useFlatListIssues = createStateHook(Store, {
	selector: getFlatListIssuesSelector,
});

const getFlatListIssueIdsSelector = (state: State) => state.flatListIssueIds;

export const useFlatListIssueIds = createStateHook(Store, {
	selector: getFlatListIssueIdsSelector,
});

const getFlatListChildIssueIdsSelector = (state: State) => state.flatListChildIssueIds;

export const useFlatListChildIssueIds = createStateHook(Store, {
	selector: getFlatListChildIssueIdsSelector,
});
